import React, { createContext, useContext, useState, useEffect } from 'react';
import { Product, ProductVariant, CartItem, Currency, Locale, ShippingConfig, StoreStatus } from '../types';
import { translations } from '../i18n/translations';

interface ShopContextType {
  locale: Locale;
  setLocale: (l: Locale) => void;
  currency: Currency;
  setCurrency: (c: Currency) => void;
  t: typeof translations.fr;
  products: Product[];
  currentProduct: Product | null;
  shippingConfig: ShippingConfig | null;
  storeStatus: StoreStatus | null;
  cart: CartItem[];
  cartCount: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  legalModalType: string | null;
  setLegalModalType: (type: string | null) => void;
  toastMessage: string | null;
  addToCart: (product: Product, variant?: ProductVariant, quantity?: number) => void;
  updateQuantity: (productId: string, variantId: string | undefined, qty: number) => void;
  removeFromCart: (productId: string, variantId?: string) => void;
  clearCart: () => void;
  refreshProducts: () => Promise<void>;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

export const ShopProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocale] = useState<Locale>('fr');
  const [currency, setCurrency] = useState<Currency>('CHF');
  const [products, setProducts] = useState<Product[]>([]);
  const [shippingConfig, setShippingConfig] = useState<ShippingConfig | null>(null);
  const [storeStatus, setStoreStatus] = useState<StoreStatus | null>(null);
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('fts_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [legalModalType, setLegalModalType] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem('fts_cart', JSON.stringify(cart));
    } catch {}
  }, [cart]);

  const refreshProducts = async () => {
    try {
      const res = await fetch('/api/products');
      if (res.ok) {
        const data = await res.json() as any;
        setProducts(data.products || []);
        setShippingConfig(data.shipping_config || null);
        setStoreStatus(data.store_status || null);
      }
    } catch (err) {
      console.error('Failed to load products from API:', err);
    }
  };

  useEffect(() => {
    refreshProducts();
  }, []);

  const t = translations[locale] || translations.fr;
  const currentProduct = products[0] || null;

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const addToCart = (product: Product, variant?: ProductVariant, quantity = 1) => {
    setCart(prev => {
      const existingIdx = prev.findIndex(
        it => it.productId === product.id && (it.variantId || '') === (variant?.id || '')
      );
      if (existingIdx > -1) {
        const copy = [...prev];
        copy[existingIdx].quantity += quantity;
        return copy;
      }
      return [...prev, {
        productId: product.id,
        variantId: variant?.id,
        quantity,
        product,
        variant
      }];
    });

    showToast(locale === 'de' ? 'Zum Warenkorb hinzugefügt!' : 'Ajouté au panier !');
    setIsCartOpen(true);
  };

  const updateQuantity = (productId: string, variantId: string | undefined, qty: number) => {
    if (qty <= 0) {
      removeFromCart(productId, variantId);
      return;
    }
    setCart(prev =>
      prev.map(it => {
        if (it.productId === productId && (it.variantId || '') === (variantId || '')) {
          return { ...it, quantity: Math.min(99, qty) };
        }
        return it;
      })
    );
  };

  const removeFromCart = (productId: string, variantId?: string) => {
    setCart(prev => prev.filter(it => !(it.productId === productId && (it.variantId || '') === (variantId || ''))));
  };

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((acc, it) => acc + it.quantity, 0);

  return (
    <ShopContext.Provider
      value={{
        locale,
        setLocale,
        currency,
        setCurrency,
        t,
        products,
        currentProduct,
        shippingConfig,
        storeStatus,
        cart,
        cartCount,
        isCartOpen,
        setIsCartOpen,
        legalModalType,
        setLegalModalType,
        toastMessage,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        refreshProducts
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = () => {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error('useShop must be used within ShopProvider');
  return ctx;
};
